import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import {
  getRepairOrders,
  getRepairOrderStatuses,
  getRepairOrderModels,
} from "../../../../services/repairOrders";

// Helper function to check if body is empty
const isBodyEmpty = (body) => {
  if (!body) return true;
  if (Array.isArray(body)) return body.length === 0;
  if (typeof body === 'object') return Object.keys(body).length === 0;
  return true;
};

const RepairOrdersContext = createContext();

const initialState = {
  baseOrders: [],
  baseMeta: null,
  basePartyUuid: null,
  orders: [],
  ordersMeta: null,
  statuses: [],
  models: [],
  loading: false,
  filtersLoading: false,
  error: null,
  isEmpty: false,
};

export const RepairOrdersProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const fetchRequestIdRef = useRef(0);

  const setPartialState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const fetchRepairOrders = useCallback(
    async (params = {}, options = {}) => {
      const { persistAsBase = false } = options;
      const requestId = ++fetchRequestIdRef.current;
      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        isEmpty: false,
      }));

      try {
        const response = await getRepairOrders(params);

        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        const responseStatusCode = response?.statusCode;
        const isValidResponse = responseStatusCode === 200 || responseStatusCode === 201;

        if (!isValidResponse) {
          const errorMessage = response?.msgCode?.[0]?.description || 'Invalid response status code';
          throw new Error(errorMessage);
        }

        const body = response?.body;
        
        if (isBodyEmpty(body)) {
          setState((prev) => ({
            ...prev,
            baseOrders: persistAsBase ? [] : prev.baseOrders,
            baseMeta: persistAsBase ? null : prev.baseMeta,
            basePartyUuid: persistAsBase ? null : prev.basePartyUuid,
            orders: [],
            ordersMeta: response.meta || null,
            loading: false,
            error: null,
            isEmpty: true,
          }));
        } else {
          const orders = Array.isArray(body) ? body : [];
          const meta = response.meta || null;
          const partyUuid = params?.party_uuid ?? null;

          setState((prev) => ({
            ...prev,
            baseOrders: persistAsBase ? orders : prev.baseOrders,
            baseMeta: persistAsBase ? meta : prev.baseMeta,
            basePartyUuid: persistAsBase ? partyUuid : prev.basePartyUuid,
            orders,
            ordersMeta: meta,
            loading: false,
            error: null,
            isEmpty: false,
          }));
        }
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        console.error("Error fetching repair orders:", error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error,
          orders: [],
          ordersMeta: null,
          isEmpty: false,
          ...(persistAsBase
            ? {
                baseOrders: [],
                baseMeta: null,
                basePartyUuid: null,
              }
            : {}),
        }));
      }
    },
    []
  );

  const fetchStatuses = useCallback(
    async (partyUuid, search = null) => {
      setPartialState({ filtersLoading: true });
      try {
        const response = await getRepairOrderStatuses(partyUuid, search);
        setPartialState({
          statuses: Array.isArray(response.body) ? response.body : [],
          filtersLoading: false,
        });
      } catch (error) {
        console.error("Error fetching repair order statuses:", error);
        setPartialState({
          statuses: [],
          filtersLoading: false,
        });
      }
    },
    [setPartialState]
  );

  const fetchModels = useCallback(
    async (partyUuid, search = null) => {
      setPartialState({ filtersLoading: true });
      try {
        const response = await getRepairOrderModels(partyUuid, search);
        setPartialState({
          models: Array.isArray(response.body) ? response.body : [],
          filtersLoading: false,
        });
      } catch (error) {
        console.error("Error fetching repair order models:", error);
        setPartialState({
          models: [],
          filtersLoading: false,
        });
      }
    },
    [setPartialState]
  );

  const setFilteredOrders = useCallback((orders = [], meta = null) => {
    setState((prev) => ({
      ...prev,
      orders: Array.isArray(orders) ? orders : [],
      ordersMeta: meta || null,
    }));
  }, []);

  const resetOrdersToBase = useCallback(() => {
    
    fetchRequestIdRef.current = fetchRequestIdRef.current + 1;
    
    setState((prev) => ({
      ...prev,
      orders: prev.baseOrders,
      ordersMeta: prev.baseMeta,
      loading: false,
    }));
  }, []);

  const resetRepairOrders = useCallback(() => {
    setState(initialState);
    fetchRequestIdRef.current = 0;
  }, []);

  const value = {
    baseOrders: state.baseOrders,
    baseMeta: state.baseMeta,
    basePartyUuid: state.basePartyUuid,
    orders: state.orders,
    ordersMeta: state.ordersMeta,
    statuses: state.statuses,
    models: state.models,
    loading: state.loading,
    filtersLoading: state.filtersLoading,
    error: state.error,
    isEmpty: state.isEmpty,
    fetchRepairOrders,
    fetchStatuses,
    fetchModels,
    resetRepairOrders,
    setFilteredOrders,
    resetOrdersToBase,
  };

  return (
    <RepairOrdersContext.Provider value={value}>
      {children}
    </RepairOrdersContext.Provider>
  );
};

export const useRepairOrders = () => {
  const context = useContext(RepairOrdersContext);
  if (!context) {
    throw new Error("useRepairOrders must be used within RepairOrdersProvider");
  }
  return context;
};

